import { useEffect } from 'react';
import { ReactSVG } from 'react-svg'
import cn from 'classnames';
import { getImage } from '../../../../utilities/iconResolver'
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { setCurrentStep, setDynamicAnswer } from '../../onboardingSlice';
import Checkbox from '../../../customize-button/components/form/Checkbox';
import { t } from '../../../../utilities/translator';
import { pushEvent } from '../../../../utilities/analytics';
import { WIZARD_DYNAMIC_QUESTION_ANSWERED, WIZARD_DYNAMIC_QUESTION_LOADED } from '../../../../constants/analyticEvents';
import { createPortal } from 'react-dom';

type DynamicStepProps = {
  data: {
    id: number;
    type: string;
    answer_type: 'SINGLE' | 'MULTIPLE';
    text: string;
    name: string;
    priority: number;
    answers: {
      id: number;
      text: string;
      is_default: boolean;
    }[]
  },
  counterLabel: string;
}

const DynamicStep = (props: DynamicStepProps) => {
  const dispatch = useAppDispatch();
  const { data, counterLabel } = props;
  const savedAnswer = useAppSelector(state => state.onboarding.dynamicAnswers) as { [key: number]: number[] };
  const currentAnswer = savedAnswer[data.id] || [];
  const defaultAnswer = data.answers.find(answer => answer.is_default)?.id;
  const footerPortal = document.getElementById('wizard-next-button');

  useEffect(() => {
    pushEvent(WIZARD_DYNAMIC_QUESTION_LOADED, {
      name: data.name
    })
  }, [data])

  const setDefaultAnswer = () => {
    if (defaultAnswer) {
      dispatch(setDynamicAnswer({
        id: data.id,
        answer: [defaultAnswer],
      }))
    }
  }

  const handleContinueClick = () => {
    if (defaultAnswer && savedAnswer[data.id]?.length === 0) {
      setDefaultAnswer();
    }

    pushEvent(WIZARD_DYNAMIC_QUESTION_ANSWERED, {
      name: data.name,
      answer: savedAnswer[data.id],
    })

    dispatch(setCurrentStep(1));
  }

  const handleNotRelevantClick = () => {
    if (defaultAnswer) {
      setDefaultAnswer();
    }

    pushEvent(WIZARD_DYNAMIC_QUESTION_ANSWERED, {
      name: data.name,
      answer: defaultAnswer || 'not relevant',
    })

    dispatch(setCurrentStep(1));
  }

  const handleOptionClick = (id: number) => {
    let answer;
    if (data.answer_type === 'SINGLE') {
      answer = [id]
      setTimeout(handleContinueClick, 300);
    }

    if (data.answer_type === 'MULTIPLE') {
      if (currentAnswer.includes(id)) {
        answer = currentAnswer.filter((item: number) => item !== id && item !== defaultAnswer);
      } else {
        answer = [id, ...currentAnswer.filter((item: number) => item !== defaultAnswer)];
      }
    }

    dispatch(setDynamicAnswer({
      id: data.id,
      answer,
    }))
  }

  return (
    <div>
      <div className="wizard-step gap-10 wizard-question">
        <div className="text-center">
          <ReactSVG src={getImage('hotel-label.svg')} />
        </div>
        <div className="flex flex-col gap-3 text-center items-center">
          <span className="text-tertiary font-medium text-sm">{counterLabel}</span>
          <div className="wizard-step__title">{data.text}</div>
        </div>
        <div className="flex flex-col gap-5">
          {data.answers.filter(answer => !answer.is_default).map((answer) => (
            <div key={answer.id} className={cn('wizard-checkbox-option', { selected: currentAnswer.includes(answer.id) })} onClick={() => handleOptionClick(answer.id)}>
              <Checkbox
                label={answer.text}
                wrapClassNames="pointer-events-none"
                checked={currentAnswer.includes(answer.id)}
              />
            </div>
          ))}
        </div>
        <div className="text-center text-tertiary text-sm underline cursor-pointer" onClick={handleNotRelevantClick}>
          {t('onboarding.notRelevant')}
        </div>

        {footerPortal ? createPortal(
          <button className="wizard-control-button" onClick={handleContinueClick}>{t('onboarding.continue')}</button>,
          footerPortal
        ) : (
          <div className="wizard-footer">
            <button className="wizard-control-button" onClick={handleContinueClick}>{t('onboarding.continue')}</button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DynamicStep
